import { IntlShape, useIntl } from "react-intl"
import ResponseError from "#/types/responseError"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { carrotClient } from "#/utils/CarrotClient"
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"

export interface TelemedicineForPediatricsEligibility {
  eligibilityRecordId: string | null
  registrationCompleted: boolean
  enrollmentUrl: string
}

export const GET_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY = ["getTelemedicineForPediatricsEligibility"]
const CREATE_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY = ["createTelemedicineForPediatricsEligibility"]

function useTelemedicineForPediatricsEligibility(): UseQueryResult<
  TelemedicineForPediatricsEligibility,
  ResponseError
> {
  return useQuery({
    queryKey: GET_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY,
    queryFn: () => carrotClient.getTelemedicineForPediatricsEligibility(),
    retry: buildCarrotApiRetryPolicy(1),
    refetchOnWindowFocus: false
  })
}

export function useCreateTelemedicineForPediatricsEligibility(): UseMutationResult<
  TelemedicineForPediatricsEligibility,
  ResponseError,
  string
> {
  return useMutation((email: string) => carrotClient.createTelemedicineForPediatricsEligibility(email), {
    mutationKey: CREATE_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY,
    retry: false
  })
}

export function useInvalidateTelemedicineForPediatricsEligibility(): () => void {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: GET_TELEMEDICINE_FOR_PEDIATRICS_ELIGIBILITY_QUERY_KEY })
}

export type TelemedicineForPediatricsActivationStatus = "not-started" | "in-progress" | "activated"

function getTelemedicineForPediatricsActivationStatus(
  eligibility: TelemedicineForPediatricsEligibility | undefined
): TelemedicineForPediatricsActivationStatus {
  if (eligibility?.registrationCompleted) {
    return "activated"
  }
  if (eligibility?.eligibilityRecordId) {
    return "in-progress"
  }
  return "not-started"
}

function getCallToActionButtonText(
  intl: IntlShape,
  activationStatus: TelemedicineForPediatricsActivationStatus
): string {
  if (activationStatus === "activated") {
    return intl.formatMessage({ defaultMessage: "Access your Blueberry account" })
  }
  return intl.formatMessage({ defaultMessage: "Activate your Blueberry account" })
}

interface UseTelemedicineForPediatricsActivationStatusResult {
  activationStatus: TelemedicineForPediatricsActivationStatus
  ctaButtonText: string
  enrollmentUrl: string | undefined
  isLoading: boolean
  isError: boolean
}

export function useTelemedicineForPediatricsActivationStatus(): UseTelemedicineForPediatricsActivationStatusResult {
  const intl = useIntl()
  const { data, isLoading, isError }: UseQueryResult<TelemedicineForPediatricsEligibility, ResponseError> =
    useTelemedicineForPediatricsEligibility()
  const activationStatus = getTelemedicineForPediatricsActivationStatus(data)
  const ctaButtonText = getCallToActionButtonText(intl, activationStatus)
  return {
    activationStatus,
    ctaButtonText,
    enrollmentUrl: data?.enrollmentUrl,
    isLoading,
    isError
  }
}
